import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatErr, fmtDate, fmtDateTime, fmtMoney } from "@/lib/api";
import { isProductsOnlyTicket } from "@/lib/routeUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { X, Navigation, CheckCircle2, Trash2, Search, Pencil, Droplet, Eye, Camera, Package } from "lucide-react";
import { toast } from "sonner";
import { BulkBar, SelectAllCheckbox, RowCheckbox } from "@/components/BulkBar";
import { SortableHeader, useSort, makeComparator } from "@/components/SortableHeader";
import DatePickerField from "@/components/DatePickerField";
import TicketCreateDialog from "@/components/TicketCreateDialog";
import TicketReviewDialog from "@/components/TicketReviewDialog";
import { ticketInDates } from "@/components/TicketSearch";
import { AdminDateRangeControls } from "@/components/AdminDateRangeControls";
import { useAdminDateRange } from "@/lib/adminDateRange";
import { HoverTime } from "@/components/HoverTime";


const todayISO = () => new Date().toISOString().slice(0, 10);
const mapsUrl = (addr) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;

const STATUS_TEXT_COLOR = {
  open: "text-primary",
  pending: "text-yellow-600",
  declined: "text-destructive",
};
const ticketStatusColor = (status) => STATUS_TEXT_COLOR[status] || "text-muted-foreground";

const totalTicketGallons = (t) =>
  (t.pool_deliveries || []).reduce((s, d) => s + Number(d.gallons || 0), 0) +
  (((t.pool_deliveries || []).length === 0) ? Number(t.gallons || 0) : 0);

export default function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [settings, setSettings] = useState({ price_per_gallon: 5 });
  const [tab, setTab] = useState("open");
  const [typeMode, setTypeMode] = useState("chlorine"); // "chlorine" | "products"
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const { dateFrom, dateTo, setDateFrom, setDateTo, showAll } = useAdminDateRange();

  // Review dialog (ticket being reviewed, with _items enriched)
  const [editing, setEditing] = useState(null);

  // Sort + reschedule dialog
  const { sortKey, sortDir, toggleSort } = useSort("delivery_date", "desc");
  const [reschedulingTicket, setReschedulingTicket] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState("");

  const load = useCallback(() => {
    api.get("/tickets?slim=true").then((r) => setTickets(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Static lookups — fetched once, not on every ticket action.
  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/products?active_only=true").then((r) => setProducts(r.data));
    api.get("/settings").then((r) => setSettings(r.data));
    // Driver roster powers the per-ticket "Assign driver" dropdown. The
    // /drivers endpoint is admin-only; secretaries get an empty list and
    // simply won't see the dropdown.
    api.get("/drivers").then((r) => setDrivers(r.data)).catch(() => setDrivers([]));
  }, []);

  const assignDriver = async (t, driverId) => {
    try {
      await api.patch(`/tickets/${t.id}/assign`, { driver_id: driverId || null });
      toast.success(driverId ? "Driver assigned" : "Driver unassigned");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  // Global "enforce driver assignment" toggle — when OFF, every driver sees
  // every open ticket again (legacy single-driver behavior). Saved on the
  // settings doc so the backend can honor it on `GET /api/tickets`.
  const toggleEnforceAssignment = async (v) => {
    setSettings((prev) => ({ ...prev, enforce_driver_assignment: v }));
    try {
      const r = await api.put("/settings", { ...settings, enforce_driver_assignment: v });
      setSettings(r.data);
      toast.success(v ? "Drivers now see only tickets assigned to them" : "All drivers can now see every ticket");
    } catch (e) {
      setSettings((prev) => ({ ...prev, enforce_driver_assignment: !v }));
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const filtered = useMemo(() => {
    const dated = tickets.filter((t) => ticketInDates(t, dateFrom, dateTo, { includeUpdated: true }));
    let arr = tab === "all" ? dated : dated.filter((t) => t.status === tab);
    // Chlorine vs Products lane. Products lane = tickets created via Orders → fulfill_as=ticket.
    arr = arr.filter((t) => (typeMode === "products" ? isProductsOnlyTicket(t) : !isProductsOnlyTicket(t)));
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((t) =>
        (t.customer_name || "").toLowerCase().includes(q) ||
        (t.business_name || "").toLowerCase().includes(q) ||
        (t.notes || "").toLowerCase().includes(q)
      );
    }
    arr.sort(makeComparator((t) => {
      switch (sortKey) {
        case "customer": return (t.business_name || t.customer_name || "").toLowerCase();
        case "delivery_date": return (t.delivery_date || "");
        case "gallons": return totalTicketGallons(t);
        case "status": return (t.status || "").toLowerCase();
        default: return "";
      }
    }, sortDir));
    return arr;
  }, [tickets, tab, typeMode, search, sortKey, sortDir, dateFrom, dateTo]);

  const datedTickets = useMemo(() => tickets.filter((t) => ticketInDates(t, dateFrom, dateTo, { includeUpdated: true })), [tickets, dateFrom, dateTo]);
  const inTab = useMemo(() => (tab === "all" ? datedTickets : datedTickets.filter((t) => t.status === tab)), [datedTickets, tab]);

  const openEdit = async (t) => {
    try {
      // List rows are slim (no photo data) — fetch the full ticket for review.
      const { data: full } = await api.get(`/tickets/${t.id}`);
      setEditing({
        ...full,
        // Always default the editable carboy qty to 0 — driver/admin actively
        // types the count they actually swapped, no accidental billing.
        refill_carboys_qty: 0,
        _items: (full.items || []).map((it) => {
          const p = products.find((x) => x.id === it.product_id);
          return { ...it, _name: p?.name || it.name || it.product_id, _price: p?.price ?? it.price ?? 0 };
        }),
      });
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const approveRequest = async (t) => {
    try {
      await api.post(`/tickets/${t.id}/approve`);
      toast.success("Request approved — now an open ticket");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const declineRequest = async (t) => {
    const reason = window.prompt("Reason (optional, customer will see this):", "") || "";
    if (reason === null) return;
    try {
      await api.post(`/tickets/${t.id}/decline`, { reason });
      toast.success("Request declined");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const rescheduleRequest = (t) => {
    setReschedulingTicket(t);
    setRescheduleDate((t.delivery_date || "").slice(0, 10) || todayISO());
  };

  const submitReschedule = async () => {
    if (!reschedulingTicket || !rescheduleDate) return;
    try {
      await api.patch(`/tickets/${reschedulingTicket.id}/date`, { delivery_date: rescheduleDate });
      toast.success("Date updated");
      setReschedulingTicket(null);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const bulkDelete = async (ids) => {
    if (!window.confirm(`Delete ${ids.length} ticket${ids.length === 1 ? "" : "s"}? Linked invoices will be deleted too.`)) return;
    try {
      await api.post("/tickets/bulk-delete", { ids });
      toast.success(`Deleted ${ids.length}`);
      setSelected([]); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const removeTicket = async (t) => {
    if (!window.confirm(`Delete this ticket?${t.invoice_id ? " (Its invoice will also be deleted.)" : ""}`)) return;
    try {
      await api.delete(`/tickets/${t.id}`);
      toast.success("Ticket deleted");
      setEditing(null); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="overline">Operations</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-1">Tickets</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 border border-border rounded-sm px-3 py-2 bg-secondary/30 cursor-pointer" data-testid="tickets-enforce-assignment-label">
            <Switch
              checked={!!settings.enforce_driver_assignment}
              onCheckedChange={toggleEnforceAssignment}
              data-testid="tickets-enforce-assignment-toggle"
            />
            <div className="text-xs leading-tight">
              <div className="font-semibold">Restrict drivers to their assigned tickets</div>
              <div className="text-[10px] text-muted-foreground">Off = every driver sees every open ticket.</div>
            </div>
          </label>
          <TicketCreateDialog customers={customers} settings={settings} onCreated={load} />
        </div>
      </div>

      {/* Reschedule dialog */}
      <Dialog open={!!reschedulingTicket} onOpenChange={(v) => { if (!v) setReschedulingTicket(null); }}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Change delivery date</DialogTitle>
            <DialogDescription>
              {reschedulingTicket ? `${reschedulingTicket.business_name || reschedulingTicket.customer_name} — currently ${(reschedulingTicket.delivery_date || "").slice(0, 10)}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <DatePickerField
              value={rescheduleDate}
              onChange={setRescheduleDate}
              testId="reschedule-date-picker"
              className="w-full"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReschedulingTicket(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={submitReschedule} className="rounded-sm" data-testid="reschedule-save">Save date</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <Tabs value={tab} onValueChange={setTab} className="overflow-x-auto -mx-1 px-1 sm:overflow-visible">
          <TabsList className="rounded-sm inline-flex w-auto">
            <TabsTrigger value="pending" data-testid="tickets-tab-pending">
              Pending ({datedTickets.filter((t) => t.status === "pending").length})
            </TabsTrigger>
            <TabsTrigger value="open" data-testid="tickets-tab-open">Open ({datedTickets.filter((t) => t.status === "open").length})</TabsTrigger>
            <TabsTrigger value="closed" data-testid="tickets-tab-closed">Closed ({datedTickets.filter((t) => t.status === "closed").length})</TabsTrigger>
            <TabsTrigger value="declined" data-testid="tickets-tab-declined">Declined ({datedTickets.filter((t) => t.status === "declined").length})</TabsTrigger>
            <TabsTrigger value="all" data-testid="tickets-tab-all">All ({datedTickets.length})</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative w-full sm:max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input className="rounded-sm pl-9" placeholder="Search customer or notes…" value={search}
                 onChange={(e) => setSearch(e.target.value)} data-testid="ticket-search-input" />
        </div>
      </div>

      <AdminDateRangeControls dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} showAll={showAll} testIdPrefix="tickets" />

      <Tabs value={typeMode} onValueChange={setTypeMode}>
        <TabsList className="rounded-sm w-full sm:w-auto">
          <TabsTrigger value="chlorine" className="flex-1 sm:flex-initial sm:px-6" data-testid="tickets-type-chlorine">
            <Droplet className="w-3.5 h-3.5 mr-1.5" /> Chlorine ({inTab.filter((t) => !isProductsOnlyTicket(t)).length})
          </TabsTrigger>
          <TabsTrigger value="products" className="flex-1 sm:flex-initial sm:px-6" data-testid="tickets-type-products">
            <Package className="w-3.5 h-3.5 mr-1.5" /> Products ({inTab.filter(isProductsOnlyTicket).length})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <BulkBar selected={selected} allIds={filtered.map((t) => t.id)} onChangeSelected={setSelected}
               onBulkDelete={bulkDelete} testId="ticket-bulk-bar" />

      <div className="border border-border overflow-x-auto">
        <div className="min-w-[820px]">
        <div className="grid grid-cols-12 px-6 py-3 border-b border-border overline items-center">
          <div className="col-span-1"><SelectAllCheckbox selected={selected} allIds={filtered.map((t) => t.id)} onChange={setSelected} testId="ticket-select-all" /></div>
          <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="customer" onToggle={toggleSort} className="col-span-3" testId="sort-customer">Customer</SortableHeader>
          <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="delivery_date" onToggle={toggleSort} className="col-span-2" testId="sort-delivery">Delivery</SortableHeader>
          <div className="col-span-2">Pools & needs</div>
          <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="gallons" onToggle={toggleSort} className="col-span-1" testId="sort-gallons">Gallons</SortableHeader>
          <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="status" onToggle={toggleSort} className="col-span-3 justify-end" testId="sort-status">Status / Actions</SortableHeader>
        </div>
        {filtered.length === 0 && <div className="px-6 py-10 text-center text-sm text-muted-foreground">{search ? "No matches." : `No ${typeMode === "products" ? "product" : "chlorine"} tickets.`}</div>}
        {filtered.map((t) => {
          const totalGallons = totalTicketGallons(t);
          const firstAddr = t.pool_deliveries?.[0]?.pool_address || t.customer_address;
          const isProductsOnly = isProductsOnlyTicket(t);
          const photoCount = (t.pool_deliveries || []).reduce((s, d) => s + (d.has_photo_before ? 1 : 0) + (d.has_photo_after ? 1 : 0), 0);
          return (
            <div key={t.id} className="grid grid-cols-12 px-6 py-3 border-b border-border last:border-b-0 hover:bg-secondary/40 items-center" data-testid={`ticket-row-${t.id}`}>
              <div className="col-span-1"><RowCheckbox id={t.id} selected={selected} onChange={setSelected} testId={`ticket-check-${t.id}`} /></div>
              <div className="col-span-3">
                <div className="font-medium">{t.business_name || t.customer_name}</div>
                {t.business_name && <div className="text-xs text-muted-foreground">{t.customer_name}</div>}
              </div>
              <div className="col-span-2 mono-num text-sm"><HoverTime iso={t.delivery_date} detail={t.created_at ? `Delivery ${fmtDate(t.delivery_date)} · Created ${fmtDateTime(t.created_at)}` : ""} testId={`ticket-date-${t.id}`} /></div>
              <div className="col-span-2 text-sm">
                {(t.pool_deliveries?.length || 0) > 0 ? (
                  <div className="space-y-1">
                    <div><span className="mono-num">{t.pool_deliveries.length}</span> pool{t.pool_deliveries.length === 1 ? "" : "s"}</div>
                    {t.refill_empty_jugs && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wider" data-testid={`ticket-jugs-${t.id}`}>
                        <Droplet className="w-3 h-3" /> Refill 5-gal jugs
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="space-y-1">
                    <span className="text-muted-foreground">{(t.items || []).length > 0 ? "" : "No pools"}</span>
                    {(t.items || []).length > 0 && (
                      <div><span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-foreground text-background text-[10px] font-bold uppercase tracking-wider" data-testid={`ticket-products-${t.id}`}>
                        <Package className="w-3 h-3" /> {(t.items || []).map((i) => `${Number(i.quantity)}× ${i.name || "Product"}`).join(", ")}
                      </span></div>
                    )}
                    {t.refill_empty_jugs && !isProductsOnly && (
                      <div><span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wider" data-testid={`ticket-jugs-${t.id}`}>
                        <Droplet className="w-3 h-3" /> Refill 5-gal jugs
                      </span></div>
                    )}
                  </div>
                )}
              </div>
              <div className="col-span-1 mono-num text-sm flex items-center gap-2">
                {totalGallons}
                {photoCount > 0 && (
                  <span
                    title={`${photoCount} delivery photo${photoCount === 1 ? "" : "s"}`}
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm bg-primary/10 text-primary text-[10px] font-bold leading-none"
                    data-testid={`ticket-photo-badge-${t.id}`}
                  >
                    <Camera className="w-3 h-3" />
                    {photoCount > 1 && <span className="mono-num">{photoCount}</span>}
                  </span>
                )}
              </div>
              <div className="col-span-3 flex justify-end items-center gap-1 flex-wrap">
                <span className={`text-xs font-bold uppercase tracking-wider mr-1 ${ticketStatusColor(t.status)}`}>
                  {t.status}
                </span>
                {t.customer_requested && t.status === "pending" && (
                  <span className="text-[10px] text-muted-foreground mr-1 font-mono uppercase tracking-wider">From portal</span>
                )}
                {t.auto_generated && <span className="text-[10px] text-muted-foreground mr-1">AUTO</span>}
                {/* Driver assignment — only shown for live tickets and when the
                    drivers roster is available (admin/secretary). */}
                {(t.status === "open" || t.status === "pending") && drivers.length > 0 && (
                  <select
                    value={t.assigned_driver_id || ""}
                    onChange={(e) => assignDriver(t, e.target.value)}
                    className="h-8 rounded-sm border border-border bg-background text-xs px-2 max-w-[140px]"
                    title={t.assigned_driver_id ? `Assigned to ${t.assigned_driver_name || "driver"}` : "Assign a driver"}
                    data-testid={`ticket-assign-${t.id}`}
                  >
                    <option value="">Unassigned</option>
                    {drivers.map((d) => (
                      <option key={d.id} value={d.id}>{d.name || d.email}</option>
                    ))}
                  </select>
                )}
                {firstAddr && t.status !== "pending" && t.status !== "declined" && (
                  <a href={mapsUrl(firstAddr)} target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-1 px-2 h-8 rounded-sm bg-foreground text-background text-xs font-semibold hover:bg-foreground/90"
                     data-testid={`ticket-navigate-${t.id}`}>
                    <Navigation className="w-3.5 h-3.5" /> Drive
                  </a>
                )}
                {t.status === "pending" && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => rescheduleRequest(t)} className="rounded-sm h-8" data-testid={`ticket-reschedule-${t.id}`}>
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Date
                    </Button>
                    <Button size="sm" variant="default" onClick={() => approveRequest(t)} className="rounded-sm h-8 bg-success hover:bg-success/90 text-white" data-testid={`ticket-approve-${t.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => declineRequest(t)} className="rounded-sm h-8 text-destructive hover:text-destructive" data-testid={`ticket-decline-${t.id}`}>
                      <X className="w-3.5 h-3.5 mr-1" /> Decline
                    </Button>
                  </>
                )}
                {t.status === "open" && (
                  <Button size="sm" variant="default" onClick={() => openEdit(t)} className="rounded-sm h-8" data-testid={`ticket-close-${t.id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {isProductsOnly ? "Review & close" : "Review"}
                  </Button>
                )}
                {t.status === "closed" && (
                  <Button size="sm" variant="outline" onClick={() => openEdit(t)} className="rounded-sm h-8" data-testid={`ticket-view-${t.id}`}>
                    <Eye className="w-3.5 h-3.5 mr-1" /> View
                  </Button>
                )}
                {t.status === "closed" && t.invoice_id && (
                  <Link to={`/admin/invoices/${t.invoice_id}`} className="text-xs text-primary hover:underline self-center px-2" data-testid={`ticket-view-invoice-${t.id}`}>
                    View invoice
                  </Link>
                )}
                <Button size="sm" variant="ghost" onClick={() => removeTicket(t)} className="rounded-sm text-destructive hover:text-destructive" data-testid={`ticket-delete-${t.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          );
        })}
        </div>
      </div>

      <TicketReviewDialog
        editing={editing}
        setEditing={setEditing}
        products={products}
        settings={settings}
        onChanged={load}
        onDelete={removeTicket}
      />
    </div>
  );
}
